/**
 * RegionManager — arquitetura ESCALÁVEL de regiões (Fase 8). Fonte única do estado de
 * mundo: região atual, regiões desbloqueadas, fog e descobertas POR REGIÃO. Coordena a
 * troca de ambiente (mostra/esconde grupos), aplica o tema visual (luz/névoa/chão) e
 * fornece a tabela de inimigos/sub-áreas da região ativa. Sem "if region === ..." espalhado.
 */
import { GameConfig, type WorldRegionDef, type SubAreaDef } from "../config/GameConfig";
import { FogOfWar } from "./FogOfWar";
import type { Renderer } from "./Renderer";
import type { Arena } from "./Arena";
import type { Regions } from "./Regions";
import type { ShatteredWilds } from "./ShatteredWilds";
import type { TempestReach } from "./TempestReach";
import type { VoidAltar } from "./VoidAltar";
import type { VoidBarrier } from "./VoidBarrier";
import { riftVariantFor, type RiftGate } from "./RiftGate";
import { i18n } from "../i18n/i18n";
import type { ChestManager } from "./ChestManager";
import type { Sanctuary } from "./Sanctuary";
import type { ColossusGate } from "./ColossusGate";

export interface RegionDeps {
  /** 10.6L — muros/portas entre territórios (opcional). */
  terrGates?: { setRegion(id: string): void };
  renderer: Renderer;
  arena: Arena;
  forestBands: Regions;
  wilds: ShatteredWilds;
  tempest: TempestReach;
  altar: VoidAltar;
  gate: VoidBarrier;
  rift: RiftGate;
  chests: ChestManager;
  sanctuary: Sanctuary;
  colossusGate: ColossusGate;
}

export interface WorldSnapshot {
  unlockedRegions: string[];
  currentRegion: string;
  fogByRegion: Record<string, number[]>;
  /** 10.9HQ — se veio de um save com o grid antigo (fixo), a extensão dele (pra migrar os índices). */
  fogFromExtent?: number;
  discoveriesByRegion: Record<string, Record<string, boolean>>;
}

export class RegionManager {
  private readonly defs = GameConfig.world.regions;
  private currentId: string = GameConfig.world.startRegion;
  private readonly unlocked = new Set<string>([GameConfig.world.startRegion]);
  private readonly fog = new Map<string, FogOfWar>();
  private readonly discoveries = new Map<string, Record<string, boolean>>();

  constructor(private readonly deps: RegionDeps) {
    for (const r of this.defs) {
      this.fog.set(r.id, new FogOfWar(r.arenaHalfSize + 8)); // 10.9HQ — grid do tamanho da área
      this.discoveries.set(r.id, {});
    }
  }

  get id(): string {
    return this.currentId;
  }
  current(): WorldRegionDef {
    return this.defById(this.currentId);
  }
  defById(id: string): WorldRegionDef {
    return this.defs.find((r) => r.id === id) ?? this.defs[0];
  }
  isUnlocked(id: string): boolean {
    return this.unlocked.has(id);
  }
  unlock(id: string): void {
    this.unlocked.add(id);
  }
  /** 10.9L3 — o jogador já andou nessa região? (névoa revelada ou alguma descoberta) */
  visited(id: string): boolean {
    const d = this.discoveries.get(id);
    return (this.fog.get(id)?.snapshot().length ?? 0) > 0 || (!!d && Object.values(d).some(Boolean));
  }
  /** 10.9L3 — tira a liberação (região aberta pelo atalho antigo do boss, sem as missões da área). */
  lock(id: string): void {
    if (id !== this.defs[0].id) this.unlocked.delete(id); // a 1ª região é sempre aberta
  }
  get unlockedList(): string[] {
    return [...this.unlocked];
  }

  activeFog(): FogOfWar {
    return this.fog.get(this.currentId)!;
  }
  activeDiscoveries(): Record<string, boolean> {
    return this.discoveries.get(this.currentId)!;
  }
  /** 10.9L6 — descobertas de QUALQUER região (a missão "explorar" é recalculada no load pra todas). */
  discoveriesOf(id: string): Record<string, boolean> {
    return this.discoveries.get(id) ?? {};
  }
  /** Marca uma descoberta na região ATUAL; true na primeira vez. */
  discover(key: string): boolean {
    const d = this.activeDiscoveries();
    if (d[key]) return false;
    d[key] = true;
    return true;
  }

  enemyTable(): { id: string; weight: number }[] {
    return this.current().enemyTable;
  }
  maxAlive(): number {
    return this.current().maxAlive;
  }

  /** Sub-área (faixa de Z) na posição atual — para o banner/descobertas. */
  subAreaAt(z: number): SubAreaDef {
    const subs = this.current().subAreas;
    for (const s of subs) if (z >= s.zMin && z < s.zMax) return s;
    return subs[Math.floor(subs.length / 2)] ?? subs[0];
  }

  /** Aplica ambiente + tema da região informada (não mexe em inimigos/loot — isso é do Game). */
  activate(id: string): WorldRegionDef {
    const def = this.defById(id);
    this.currentId = id;
    const isForest = id === "forgottenForest";
    const isWilds = id === "shatteredWilds";
    const isTempest = id === "tempestReach";
    const t = def.theme;

    // 10.5S — o Arena agora é o CHÃO de todas as regiões (bioma/props/colisão por território);
    // ShatteredWilds/TempestReach cuidam só dos marcos (ruínas, platôs, cristais gigantes).
    this.deps.arena.setRegion(id);
    this.deps.terrGates?.setRegion(id); // 10.6L: muros e portas entre territórios
    this.deps.arena.group.visible = true;
    this.deps.forestBands.group.visible = isForest;
    this.deps.wilds.setVisible(isWilds);
    this.deps.tempest.setVisible(isTempest); // Tempest Reach (Região 3)
    this.deps.colossusGate.setVisible(isWilds); // Colossus Gate é SÓ da Região 2
    this.deps.altar.setVisible(isForest); // altar é da Região 1
    this.deps.gate.setRegionActive(isForest); // gate do boss é da Região 1

    // Rift Gate da região (posição/tema/ativação)
    const rc = def.rift;
    if (rc) {
      this.deps.rift.setVisible(true);
      this.deps.rift.setPosition(rc.x, rc.z);
      this.deps.rift.setAccent(t.accent);
      this.deps.rift.setVariant(riftVariantFor(id)); // 10.9BS: cor de energia + adornos da região
      // ativo se não exige boss OU se o boss já caiu (o Game reconfirma via progress)
      this.deps.rift.setActive(!rc.requiresBoss);
      // 10.9GU — placa: "Portal Rift" + destino e nível recomendado
      const target = GameConfig.world.regions.find((r) => r.id === rc.toRegion) as WorldRegionDef | undefined;
      this.deps.rift.setLabel(i18n.t("rift.gate"), target ? i18n.t("rift.sub", { name: i18n.t(target.nameKey), n: String(target.recommendedLevel) }) : undefined);
    } else {
      this.deps.rift.setVisible(false);
    }

    // tema visual
    this.deps.renderer.applyTheme({ ambient: t.ambient, ambientIntensity: t.ambientIntensity, sun: t.sun, sunIntensity: t.sunIntensity, fog: t.fog });
    // 10.9BS — céu/névoa/densidade próprios da região (fallback: névoa do tema, densidade padrão)
    this.deps.renderer.setRegionMood(def.mood ?? { sky: t.fog, fog: t.fog, density: 0.0085 });
    this.deps.arena.setGroundColor(t.ground);
    this.deps.arena.setWallTheme(t.stone, t.accent); // 10.6K: muralha com a cara da região
    this.deps.chests.setRegion(id);
    this.deps.sanctuary.applyRegionTheme(id); // Sanctuary temática (Fase 8.1)
    return def;
  }

  // ---- persistência (save v6) ----
  snapshot(): WorldSnapshot {
    const fogByRegion: Record<string, number[]> = {};
    const discoveriesByRegion: Record<string, Record<string, boolean>> = {};
    for (const r of this.defs) {
      fogByRegion[r.id] = this.fog.get(r.id)!.snapshot();
      discoveriesByRegion[r.id] = { ...this.discoveries.get(r.id)! };
    }
    return { unlockedRegions: this.unlockedList, currentRegion: this.currentId, fogByRegion, discoveriesByRegion };
  }

  load(snap: Partial<WorldSnapshot> | undefined, seedForestFog?: number[], seedForestDisc?: Record<string, boolean>): void {
    // desbloqueios
    this.unlocked.clear();
    this.unlocked.add(GameConfig.world.startRegion);
    for (const id of snap?.unlockedRegions ?? []) if (this.defs.some((r) => r.id === id)) this.unlocked.add(id);

    // fog por região (com semente do save legado v5 para a Forgotten Forest)
    for (const r of this.defs) {
      const f = this.fog.get(r.id)!;
      const cells = snap?.fogByRegion?.[r.id] ?? (r.id === "forgottenForest" ? seedForestFog : undefined);
      f.load(cells, snap?.fogFromExtent); // 10.9HQ — migra do grid antigo se preciso
    }
    // descobertas por região (semente legada para a Forgotten Forest)
    for (const r of this.defs) {
      const d = snap?.discoveriesByRegion?.[r.id] ?? (r.id === "forgottenForest" ? seedForestDisc : undefined) ?? {};
      this.discoveries.set(r.id, { ...d });
    }
    // sempre inicia a sessão na região inicial (seguro); a atual salva fica registrada mas
    // a entrada de sessão é sempre pela Forgotten Forest (o Rift está lá para voltar).
    this.currentId = GameConfig.world.startRegion;
  }

  reset(): void {
    this.unlocked.clear();
    this.unlocked.add(GameConfig.world.startRegion);
    for (const r of this.defs) {
      this.fog.get(r.id)!.reset();
      this.discoveries.set(r.id, {});
    }
    this.currentId = GameConfig.world.startRegion;
  }
}
